import { registerIndicator, type IndicatorDrawParams, type KLineData } from 'klinecharts'
import type { EarningsEvent } from '../types/earnings'
import { registerOnce } from './registrationUtils'
import { sessionDayKey, DEFAULT_SESSION_TZ } from './sessionDay'
import { CHART_FONT_FAMILY } from './chartColors'

/**
 * Earnings markers - a small outlined "E" badge pinned to the BOTTOM of the
 * price pane at each earnings session day, colored beat (green) / miss (red) /
 * neutral (gray). The badge is an OUTLINE: transparent inside, with a 1px
 * border and the letter in the event color. A solid filled badge reads too
 * loud against the candles; details live in the hover card.
 * Rendered as a SYSTEM custom-draw indicator (mirroring
 * `sessionBreaks.ts` / `previousClose.ts`), NOT a user overlay: it's chart
 * chrome, so it's excluded from the `s` hide-all toggle via
 * `SYSTEM_INDICATOR_NAMES` and never syncs through the drawing store.
 *
 * The earnings array + session timezone ride in via the indicator's
 * `extendData` (same pattern the session indicators use), so `draw` can be a
 * pure function of the loaded bars + the extendData — it redraws on pan/zoom
 * for free.
 */

const INDICATOR_NAME = 'EARNINGS_MARKERS'

/** Beat/miss/neutral palette (matches the research earnings chart). */
export const BEAT_COLOR = '#22c55e'
export const MISS_COLOR = '#ef4444'
export const NEUTRAL_COLOR = '#787b86'

export interface EarningsMarkersConfig {
  earnings: EarningsEvent[]
  timezone: string
}

/**
 * Beat/miss/neutral color for an earnings event. Prefers the explicit EPS
 * surprise percent; falls back to actual-vs-estimate when the surprise isn't
 * supplied. A zero surprise (or missing/unusable data) is neutral gray.
 */
export function beatMissColor(ev: EarningsEvent): string {
  const surprise = ev.epsSurprisePct
  if (surprise != null && Number.isFinite(surprise)) {
    if (surprise > 0) return BEAT_COLOR
    if (surprise < 0) return MISS_COLOR
    return NEUTRAL_COLOR
  }
  const { epsActual, epsEstimate } = ev
  if (
    epsActual != null &&
    epsEstimate != null &&
    Number.isFinite(epsActual) &&
    Number.isFinite(epsEstimate)
  ) {
    if (epsActual > epsEstimate) return BEAT_COLOR
    if (epsActual < epsEstimate) return MISS_COLOR
  }
  return NEUTRAL_COLOR
}

/**
 * First bar index whose session day (in `tz`) equals the earnings `date`, or
 * -1 when no loaded bar falls on that day. Reuses the same ET session-day
 * bucketing (`sessionDayKey`, en-CA → YYYY-MM-DD) that pivots / session breaks
 * use, so the earnings `date` string compares directly against a bar's key.
 * Skipping (-1) is the caller's cue to drop earnings outside the loaded range
 * — or, on weekly+ charts, a day that no single aggregated bar lands on.
 */
export function earningsBarIndex(
  dataList: Array<{ timestamp: number } | undefined>,
  date: string,
  tz: string = DEFAULT_SESSION_TZ,
): number {
  for (let i = 0; i < dataList.length; i++) {
    const bar = dataList[i]
    if (!bar) continue
    if (sessionDayKey(bar.timestamp, tz) === date) return i
  }
  return -1
}

/** Badge geometry — a compact rounded pill sitting just above the pane bottom. */
const BADGE_W = 14
const BADGE_H = 14
const BADGE_BOTTOM_GAP = 4

/** Minimal 2D-context surface `drawEarningsMarkers` needs — lets a fake ctx
 *  (see the unit test) record fillRect / fillText calls without a real canvas. */
export interface EarningsDrawCtx {
  save: () => void
  restore: () => void
  beginPath: () => void
  moveTo: (x: number, y: number) => void
  lineTo: (x: number, y: number) => void
  arcTo: (x1: number, y1: number, x2: number, y2: number, r: number) => void
  arc: (x: number, y: number, r: number, startAngle: number, endAngle: number) => void
  closePath: () => void
  fill: () => void
  stroke: () => void
  setLineDash: (segments: number[]) => void
  fillRect: (x: number, y: number, w: number, h: number) => void
  fillText: (text: string, x: number, y: number) => void
  fillStyle: string
  strokeStyle: string
  lineWidth: number
  font: string
  textAlign: string
  textBaseline: string
}

export interface EarningsDrawParams {
  ctx: EarningsDrawCtx
  kLineDataList: Array<{ timestamp: number } | undefined>
  visibleRange: { from: number; to: number }
  bounding: { height: number }
  xAxis: { convertToPixel: (dataIndex: number) => number }
  earnings: EarningsEvent[]
  timezone: string
}

/** Rounded-rect path helper (arcTo keeps it dependency-free + fake-ctx safe). */
function roundRect(
  ctx: EarningsDrawCtx,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

/**
 * Paint one "E" badge per in-range earnings event, at the x of the event's
 * session-day first bar, near the pane bottom. Pure over its params so it's
 * unit-testable with a fake ctx (mirrors `drawPivotSegments`). Events with no
 * matching bar in the loaded range are skipped.
 */
export function drawEarningsMarkers(params: EarningsDrawParams): boolean {
  const { ctx, kLineDataList, visibleRange, bounding, xAxis, earnings, timezone } = params
  if (!earnings.length || kLineDataList.length === 0) return true

  const { from, to } = visibleRange
  const y = bounding.height - BADGE_H - BADGE_BOTTOM_GAP

  ctx.save()
  ctx.font = `10px ${CHART_FONT_FAMILY}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  // The 2D context is shared across indicator draws and may carry a dash
  // pattern from a previous one (grid / dashed lines) - the border must be solid.
  ctx.setLineDash([])

  for (const ev of earnings) {
    const idx = earningsBarIndex(kLineDataList, ev.date, timezone)
    // Skip events with no matching bar, or bars outside the visible window.
    if (idx < 0 || idx < from || idx >= to) continue

    const cx = xAxis.convertToPixel(idx)
    const color = beatMissColor(ev)

    // Outlined badge: TRANSPARENT inside - just the 1px border and the letter
    // in the event color, so the badge sits over the chart without a plate.
    roundRect(ctx, cx - BADGE_W / 2, y, BADGE_W, BADGE_H, 3)
    ctx.strokeStyle = color
    ctx.lineWidth = 1
    ctx.stroke()

    ctx.fillStyle = color
    ctx.fillText('E', cx, y + BADGE_H / 2)
  }

  ctx.restore()
  return true
}

/** Register the `EARNINGS_MARKERS` main-pane indicator with klinecharts (once). */
export const registerEarningsMarkersIndicator = registerOnce(() => {
  registerIndicator({
    name: INDICATOR_NAME,
    shortName: '',
    // No figures: this indicator carries no series values, it only custom-draws
    // badges. `calc` is a no-op carrier so klinecharts keeps the instance alive.
    figures: [],
    calc: (dataList: KLineData[]) => dataList.map(() => ({})),
    draw: (params: IndicatorDrawParams) => {
      const { ctx, kLineDataList, visibleRange, bounding, xAxis, indicator } = params
      const config = indicator.extendData as EarningsMarkersConfig | undefined
      if (!config || !config.earnings?.length) return false

      return drawEarningsMarkers({
        ctx: ctx as unknown as EarningsDrawCtx,
        kLineDataList,
        visibleRange,
        bounding,
        xAxis,
        earnings: config.earnings,
        timezone: config.timezone || DEFAULT_SESSION_TZ,
      })
    },
  })
})

export const EARNINGS_MARKERS_NAME = INDICATOR_NAME
